﻿import { useEffect, useRef, useState } from 'react';
import { DiscretePalette } from '@fe/weather';

function DiscreteBarHorizontal(data: DiscretePalette) {
    const { name, units, values, colors } = data;
    const [maxLabelWidth, setMaxLabelWidth] = useState<number>(0);
    const [unitLabelOffset, setUnitLabelOffset] = useState<number>(0);
    const svgHeight = 15;
    const textRefs = useRef<Array<SVGTextElement | null>>([]);
    const unitTextRef = useRef<SVGTextElement | null>(null);

    const totalSegments = colors.length;

    useEffect(() => {
        //we calculate the width of the longest label and unit label to set the correct color segment width
        if (textRefs.current.length > 0) {
            const labelWidths = textRefs.current.map((text) => (text ? text.getBBox().width : 0));
            const validWidths = labelWidths.filter((width) => width > 0);
            const maxWidth = validWidths.length > 0 ? Math.max(...validWidths) + 4 : 50; // padding

            setMaxLabelWidth(maxWidth);

            if (unitTextRef.current) {
                const unitWidth = unitTextRef.current.getBBox().width + 4; // + padding
                setUnitLabelOffset(unitWidth);
            }
        }
    }, [values, units]);

    // Calculate label offset
    let currentLabelOffset = 0;
    if (values.length !== totalSegments - 1) {
        currentLabelOffset = maxLabelWidth / 2;
    } else if (values[0] === null) {
        currentLabelOffset = maxLabelWidth / 4;
    }

    const currentSegmentWidth = maxLabelWidth || 50;
    const currentUnitLabelOffset = unitLabelOffset;

    const totalSvgWidth = currentSegmentWidth * totalSegments + currentLabelOffset * 2 + currentUnitLabelOffset;

    // Calculate positions for labels
    const currentLabelPositions = values
        .map((value, index) => {
            if (value === null || value === undefined) {
                return null;
            }

            const labelIndexAdjustment = values.length === totalSegments - 1 ? 1 : 0;

            const x =
                (values[0] === null ? 0 : currentLabelOffset) +
                currentUnitLabelOffset +
                (index + labelIndexAdjustment) * currentSegmentWidth;

            return {
                value,
                x
            };
        })
        .filter((label): label is { value: number; x: number } => label !== null);

    return (
        <div style={{ textAlign: 'left', paddingBottom: '10px' }}>
            <div style={{ fontSize: 12 }}>{name}</div>
            <svg width={totalSvgWidth} height={svgHeight + 15} style={{ display: 'block' }}>
                {/* Unit Label */}
                {units && (
                    <text
                        ref={unitTextRef}
                        x={currentUnitLabelOffset / 2}
                        y={svgHeight / 2}
                        textAnchor="middle"
                        alignmentBaseline="middle"
                        fontSize="14"
                        fontWeight="500"
                        fill={'var(--text-color)'}>
                        {units}
                    </text>
                )}

                {/* Color Segments */}
                {colors.map((color, index) => (
                    <rect
                        key={index}
                        x={(values[0] === null ? 0 : currentLabelOffset) + currentUnitLabelOffset + index * currentSegmentWidth}
                        y={0}
                        width={currentSegmentWidth}
                        height={svgHeight}
                        fill={color}
                    />
                ))}

                {/* Labels */}
                {currentLabelPositions.map((label, index) => (
                    <text
                        key={index}
                        ref={(el) => {
                            textRefs.current[index] = el;
                        }}
                        x={label.x}
                        y={30}
                        textAnchor="middle"
                        alignmentBaseline="middle"
                        fontSize="10px"
                        fill={'var(--text-color)'}>
                        {label.value}
                    </text>
                ))}
            </svg>
        </div>
    );
}

export default DiscreteBarHorizontal;
